import React, { useState } from 'react';
import { useController } from '@rest-hooks/react';
import { useHistory } from 'react-router-dom';
import { Link } from '@mui/material';

import OrganizationSignupResource from 'resources/onboarding/OrganizationSignupResource';

import signUpOrganizationValidation from './Validations/signUpOrganizationForm';

import StyledTextField from 'ui/StyledComponents/StyledTextField';
import Button from 'ui/Button';
import Label from 'ui/text/Label';
import FormSection from 'ui/FormSection';

import handleSuccessfulAuth from 'utils/handleSuccessfulAuth';
import { useError } from 'utils/useErrorController';
import {
  clearPendingOrgSignup,
  getPendingOrgSignup,
  setPendingOrgSignup,
} from 'utils/pendingOrgSignup';

import { t } from 'i18n/index';

const SignUpOrganizationForm = () => {
  const { fetch } = useController();
  const history = useHistory();
  const { loading, setLoading, validate, handleError } = useError();

  const pending = getPendingOrgSignup();
  const [data, setData] = useState({
    title: pending?.title || '',
    name: pending?.name || '',
    email: pending?.email || '',
    password: '',
  });
  const [requiresPassword, setRequiresPassword] = useState(Boolean(pending?.email));
  const [resumedFromReset, setResumedFromReset] = useState(Boolean(pending?.email));
  const [requiresPasswordSetup, setRequiresPasswordSetup] = useState(false);

  const handleChange = ({ target: { name, value } }) => {
    setData((previous) => {
      const next = { ...previous, [name]: value };
      if (name === 'email') {
        next.password = '';
      }
      return next;
    });

    if (name === 'email') {
      setRequiresPassword(false);
      setResumedFromReset(false);
      setRequiresPasswordSetup(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      validate({ ...data, requires_password: requiresPassword }, signUpOrganizationValidation);

      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('name', data.name);
      formData.append('email', data.email);
      if (requiresPassword && data.password) {
        formData.append('password', data.password);
      }

      const { auth_token, user, courses, organization } = await fetch(
        OrganizationSignupResource.create(),
        formData,
      );

      handleSuccessfulAuth(auth_token, user, courses);
      clearPendingOrgSignup();
      setLoading(false);

      if (organization && organization.id) {
        history.push(`/organizations/${organization.id}/profile`);
      } else {
        history.push('/profile');
      }
    } catch (error) {
      if (error && error.response) {
        try {
          const body = await error.response.clone().json();
          if (body && body.requires_password) {
            setPendingOrgSignup({
              title: data.title,
              name: data.name,
              email: data.email,
            });
            setRequiresPassword(true);
            setLoading(false);
            return;
          }

          if (body && body.requires_password_setup) {
            setRequiresPasswordSetup(true);
            setLoading(false);
            return;
          }
        } catch (_e) {
          // fall through to default handling
        }
      }
      handleError(error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormSection slim>
        <StyledTextField
          label={t('authForms:signUpOrganizationForm:clubName')}
          name="title"
          value={data.title}
          onChange={handleChange}
          fullWidth
        />

        <StyledTextField
          label={t('authForms:signUpOrganizationForm:yourName')}
          name="name"
          value={data.name}
          onChange={handleChange}
          fullWidth
        />

        <StyledTextField
          label={t('literal:email')}
          name="email"
          type="email"
          value={data.email}
          onChange={handleChange}
          fullWidth
        />

        {requiresPassword && (
          <>
            <Label secondary padded>
              {resumedFromReset
                ? t('authForms:signUpOrganizationForm:resumedFromResetMessage')
                : <>
                    {t('authForms:signUpOrganizationForm:existingAccountPrefix')}
                    <Link
                      href="/password_resets"
                      target="_blank"
                      underline="hover"
                      color="inherit"
                      onClick={() => {
                        setPendingOrgSignup({
                          title: data.title,
                          name: data.name,
                          email: data.email,
                        });
                      }}
                    >
                      {t('authForms:signUpOrganizationForm:existingAccountResetLink')}
                    </Link>
                    .
                  </>
              }
            </Label>
            <StyledTextField
              label={t('literal:password')}
              name="password"
              type="password"
              value={data.password}
              onChange={handleChange}
              fullWidth
            />
          </>
        )}

        {requiresPasswordSetup ? (
          <Label secondary padded>
            {t('authForms:signUpOrganizationForm:passwordSetupPrefix')}
            <Link
              href="/password_resets"
              underline="hover"
              color="inherit"
              onClick={(event) => {
                event.preventDefault();
                setPendingOrgSignup({
                  title: data.title,
                  name: data.name,
                  email: data.email,
                });
                history.push('/password_resets');
              }}
            >
              {t('authForms:signUpOrganizationForm:passwordSetupLink')}
            </Link>
            .
          </Label>
        ) : (
          <Button highlighted fullwidth loading={loading} type="submit" onClick={handleSubmit}>
            {t('authForms:signUpOrganizationForm:submitButton')}
          </Button>
        )}
      </FormSection>
    </form>
  );
};

export default SignUpOrganizationForm;
